import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { userPreferencesService } from "../services/workflows.service";
import { useAuthStore } from "../store/auth.store";
import {
  decodePathologistQueueView,
  getPathologistQueueTabView,
  LEGACY_PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY,
  PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY,
  PATHOLOGIST_QUEUE_VIEW_TABS,
  replacePathologistQueueTabView,
  type PathologistQueueTabView,
  type PathologistQueueViewPreference,
  type PathologistQueueViewTab,
} from "../pages/workflows/pathologistQueueViewPreference";

type PreferenceSave = (
  key: string,
  value: Record<string, unknown>
) => Promise<void>;

const pendingSaves = new Map<
  string,
  { key: string; value: Record<string, unknown> }
>();
const runningSaves = new Map<string, Promise<void>>();

async function flushLatestPreference(
  userId: string,
  save: PreferenceSave
): Promise<void> {
  const latest = pendingSaves.get(userId);
  pendingSaves.delete(userId);
  if (!latest) return;
  await save(latest.key, latest.value);
  if (pendingSaves.has(userId)) {
    await flushLatestPreference(userId, save);
  }
}

export function queuePathologistQueueViewSave(
  userId: string,
  value: PathologistQueueViewPreference,
  save: PreferenceSave = userPreferencesService.save
): Promise<void> {
  return queueUserQueueViewSave(
    userId,
    PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY,
    value,
    save
  );
}

export function queueUserQueueViewSave(
  userId: string,
  key: string,
  value: Record<string, unknown>,
  save: PreferenceSave = userPreferencesService.save
): Promise<void> {
  const saveId = JSON.stringify([userId, key]);
  pendingSaves.set(saveId, { key, value });
  const running = runningSaves.get(saveId);
  if (running) return running;

  const run = flushLatestPreference(saveId, save).finally(() => {
    runningSaves.delete(saveId);
    const pending = pendingSaves.get(saveId);
    if (pending) {
      void queueUserQueueViewSave(userId, pending.key, pending.value, save);
    }
  });
  runningSaves.set(saveId, run);
  return run;
}

export function usePathologistQueueViewPreference() {
  const userId = useAuthStore((state) => state.profile?.id);
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ["user-preference", userId, PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY],
    [userId]
  );
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const current = await userPreferencesService.get(
        PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY
      );
      return (
        current ??
        (await userPreferencesService.get(
          LEGACY_PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY
        ))
      );
    },
    enabled: Boolean(userId),
    staleTime: Infinity,
  });
  const decoded = useMemo(
    () => decodePathologistQueueView(query.data),
    [query.data]
  );
  const invalidTabs = useMemo(
    () =>
      query.isError
        ? new Set<PathologistQueueViewTab>(PATHOLOGIST_QUEUE_VIEW_TABS)
        : decoded.invalidTabs,
    [decoded.invalidTabs, query.isError]
  );

  const updateTab = useCallback(
    (
      tab: PathologistQueueViewTab,
      updater: (
        current: PathologistQueueTabView
      ) => PathologistQueueTabView | null
    ) => {
      if (!userId || !query.isFetched) return;
      const currentRaw = queryClient.getQueryData(queryKey);
      const currentDecoded = decodePathologistQueueView(currentRaw);
      const currentTab = getPathologistQueueTabView(currentDecoded.value, tab);
      const next = replacePathologistQueueTabView(
        currentDecoded.value,
        tab,
        updater(currentTab)
      );
      queryClient.setQueryData(queryKey, next);
      void queuePathologistQueueViewSave(userId, next).catch(() => {
        void queryClient.invalidateQueries({ queryKey });
      });
    },
    [query.isFetched, queryClient, queryKey, userId]
  );

  const clearTab = useCallback(
    (tab: PathologistQueueViewTab) => updateTab(tab, () => null),
    [updateTab]
  );

  return {
    preference: decoded.value,
    invalidTabs,
    isHydrated: Boolean(userId) && query.isFetched,
    updateTab,
    clearTab,
  };
}
